import { MarkdownContent } from '@backstage/core-components';
import { LinearProgress, Tab, Tabs, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import type { CockpitArtifactContentState } from '../controller/cockpitControllerTypes';
import type { CockpitArtifactView } from '../model/cockpitViewModel';
import {
  ContentError,
  ContentFooter,
  ContentState,
  NoContent,
} from './ArtifactContentStates';
import { useCockpitStyles } from './cockpitStyles';

export interface ArtifactContentPanelProps {
  artifact: CockpitArtifactView;
  contentState?: CockpitArtifactContentState;
  loadContent?: (artifactId: string) => Promise<string>;
  loadMore?: (artifactId: string) => Promise<string>;
}

const LoadedArtifactContent = ({
  artifact,
  content,
  contentState,
  loadMore,
}: {
  artifact: CockpitArtifactView;
  content: string;
  contentState?: CockpitArtifactContentState;
  loadMore?: (artifactId: string) => Promise<string>;
}) => {
  const [tab, setTab] = useState(0);
  const loading = contentState?.status === 'loading';
  return (
    <>
      {loading && (
        <LinearProgress aria-label="Carregando conteúdo do artefato" />
      )}
      <Tabs
        value={tab}
        onChange={(_, next: number) => setTab(next)}
        aria-label="Modo de leitura do artefato"
      >
        <Tab label="Visualização" />
        <Tab label="Fonte" />
      </Tabs>
      <ArtifactReader content={content} source={tab === 1} />
      <ContentFooter
        artifactId={artifact.id}
        label={artifact.label}
        contentState={contentState}
        loadMore={loadMore}
      />
    </>
  );
};

function ArtifactReader({
  content,
  source,
}: {
  content: string;
  source: boolean;
}) {
  const classes = useCockpitStyles();
  return (
    <div className={classes.content}>
      {source ? (
        <Typography
          component="pre"
          className={classes.sourceContent}
          role="region"
          aria-label="Fonte textual do artefato"
        >
          {content}
        </Typography>
      ) : (
        <div
          className={classes.renderedArtifactContent}
          role="region"
          aria-label="Conteúdo renderizado do artefato"
        >
          <MarkdownContent content={content} />
        </div>
      )}
    </div>
  );
}

export const ArtifactContentPanel = (props: ArtifactContentPanelProps) => {
  const { artifact, contentState, loadContent } = props;
  const forbidden =
    artifact.access === 'forbidden' || contentState?.status === 'forbidden';
  const content = forbidden
    ? null
    : contentState?.page?.content ?? artifact.content ?? null;
  useAuthorizedContentLoad(artifact, content, contentState, loadContent);

  if (forbidden) {
    return (
      <ContentState>
        Conteúdo sem permissão. Metadados e linhagem permanecem visíveis.
      </ContentState>
    );
  }
  if (artifact.access === 'no-content') {
    return <NoContent hasReference={Boolean(artifact.path)} />;
  }
  if (!content && contentState?.status === 'error') {
    return <ContentError artifact={artifact} loadContent={loadContent} />;
  }
  if (content) {
    return <LoadedArtifactContent {...props} content={content} />;
  }
  return (
    <ContentState>
      {contentState?.status === 'loading'
        ? 'Carregando conteúdo permitido…'
        : 'Conteúdo ainda não carregado.'}
    </ContentState>
  );
};

function useAuthorizedContentLoad(
  artifact: CockpitArtifactView,
  content: string | null,
  state: CockpitArtifactContentState | undefined,
  loadContent: ArtifactContentPanelProps['loadContent'],
): void {
  useEffect(() => {
    if (
      artifact.access === 'inspectable' &&
      !content &&
      (!state || state.status === 'idle') &&
      loadContent
    ) {
      void loadContent(artifact.id).catch(() => undefined);
    }
  }, [artifact.access, artifact.id, content, state, loadContent]);
}
